﻿import React, { useState, useMemo, useEffect } from 'react';
import JSZip from 'jszip';
import * as Bixby from '$/core';
import { toastError, toastSuccess } from '$/store/toast/actions';
import { downloadFile } from '$/store/export/actions';
import { connect } from 'react-redux';
import { AppState } from '$/store';
import { getNonApiDocumentUploadEnabled } from '$/store/release-feature-flags/selectors';
import './index.scss';
import BixButton from '$/components/global/bixButton';
import { DocumentsAdminService } from '$/services';
import { FileValidationRequest } from '$/services/DocumentsAdminService';
import { Navigate } from 'react-router-dom';
import ClientValidation from './clientValidation';
import CompanyDocumentFileUpload from './companyDocumentFileUpload';
import { v4 as uuidv4 } from 'uuid';
import groupBy from 'lodash/groupBy';
import BixCheckbox from '$/components/global/bixCheckbox';
import Loading from '$/components/loading/Loading';

interface IDocumentsProps {
  companyDocumentUploadByEmail?: Bixby.Models.CompanyDocumentUploadEmailId;
  companyDocumentCategories: Bixby.Models.CompanyDocumentCategory[];
  currentCompany?: Bixby.Models.AdminCompany;
  isLoading: boolean;
  //hasTags?: boolean | undefined;
  isNonApiDocumentUploadEnabled: boolean;
  dispatchToastSuccess: (message: string) => Promise<void>;
  dispatchToastError: (message: string) => Promise<void>;
  dispatchDownloadDocument: (blob: Blob, fileName: string) => Promise<void>;
}

const CompanyDocumentsUpload: React.FC<IDocumentsProps> = ({
  currentCompany,
  companyDocumentUploadByEmail,
  companyDocumentCategories,
  isLoading,
  //hasTags,
  isNonApiDocumentUploadEnabled,
  dispatchToastSuccess,
  dispatchToastError,
  dispatchDownloadDocument,
}) => {
  const [droppedFiles, setDroppedFiles] = useState<Bixby.Models.IDocumentFile[]>([]); // for a zip file, all droppedFiles will have the zip file as their file attribute
  const [isValidating, setIsValidating] = useState(false);
  const [isRemovingWatermark, setIsRemovingWatermark] = useState(false);
  const [isDownloadingWatermarkRemovedFiles, setIsDownloadingWatermarkRemovedFiles] = useState(false);
  const [isDownloadingOriginalFiles, setIsDownloadingOriginalFiles] = useState(false);
  const [isFilesDownloaded, setIsFilesDownloaded] = useState(false); // is Watermark Removed Files downloaded
  const [hasBeenValidated, setHasBeenValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadSuccessful, setUploadSuccessful] = useState(false);
  const [prevalidationError, setPrevalidationError] = useState(undefined as string | undefined);
  const [validationError, setValidationError] = useState(undefined as string | undefined);
  const [uploadError, setUploadError] = useState(undefined as string | undefined);
  const [clientDataSiteProvider, setClientDataSiteProvider] = useState(
    companyDocumentUploadByEmail?.clientDataSiteProvider ?? ({} as Bixby.Models.ClientDataSiteProvider)
  );
  const [isPreValidating, setIsPrevalidating] = useState(false);
  const [sendEmailValue, setSendEmailValue] = useState(false);

  const hasEmailAlertPermissions = useMemo(() => {
    if (!currentCompany || !currentCompany.clients) return false;

    const found = currentCompany.clients.filter(_ => _.hasDocUploadAlert);
    if (clientDataSiteProvider) {
      setSendEmailValue(false);
      const wmClientFound = found.filter(_ => _.client_Name == clientDataSiteProvider.client_Name);
      //if wm upload only show if that client has alerts
      return wmClientFound !== undefined && wmClientFound.length > 0;
    } else {
      setSendEmailValue(false);
      //if company has an associated client that has news alerts enabled
      return found !== undefined && found.length > 0;
    }
  }, [clientDataSiteProvider, currentCompany]);

  const isRemoveWatermarkDisabled = useMemo(() => {
    if (droppedFiles.length === 0 || isRemovingWatermark) {
      return true;
    }

    // Scope all checks to non-client-specific files only
    const nonClientSpecificFiles = droppedFiles.filter(f => !f.uploadClientSpecific);

    // If all files are client-specific, nothing to WM-remove
    if (nonClientSpecificFiles.length === 0) {
      return true;
    }

    const invalidFiles = nonClientSpecificFiles.filter(
      file => file.hasDuplicateNameValidationError !== false || !file.title || file.hasServerValidationError
    );

    const isNoWatermarkRemovedFiles = !nonClientSpecificFiles.some(file =>
      file.fileName.toLowerCase().endsWith('.pdf')
    );

    const isNoBlobFilePath = nonClientSpecificFiles.some(file => file.blobFilePath == null);

    if (isNonApiDocumentUploadEnabled) {
      return invalidFiles.length > 0 || isNoWatermarkRemovedFiles || isNoBlobFilePath;
    } else {
      return invalidFiles.length > 0 || isNoWatermarkRemovedFiles;
    }
  }, [droppedFiles, isRemovingWatermark, isNonApiDocumentUploadEnabled]);

  const isDownloadWatermarkRemovedFilesDisabled = useMemo(() => {
    if (droppedFiles.length === 0 || isDownloadingWatermarkRemovedFiles) {
      return true;
    }

    // Scope to non-client-specific files only
    const nonClientSpecificPdfs = droppedFiles.filter(
      f => !f.uploadClientSpecific && f.blobFilePath != null && f.fileName.toLowerCase().endsWith('.pdf')
    );

    // If all files are client-specific, nothing to download
    if (nonClientSpecificPdfs.length === 0) {
      return true;
    }

    // Enabled when at least one non-client-specific PDF has isWatermarkRemoved = true
    const hasAtLeastOneWatermarkRemoved = nonClientSpecificPdfs.some(file => file.isWatermarkRemoved);

    const isNoWatermarkRemovedFiles = !droppedFiles
      .filter(f => !f.uploadClientSpecific)
      .some(file => file.fileName.toLowerCase().endsWith('.pdf'));

    const isNoBlobFilePath = droppedFiles.filter(f => !f.uploadClientSpecific).some(file => file.blobFilePath == null);

    if (isNonApiDocumentUploadEnabled) {
      return !hasAtLeastOneWatermarkRemoved || isNoWatermarkRemovedFiles || isNoBlobFilePath;
    } else {
      return !hasAtLeastOneWatermarkRemoved || isNoWatermarkRemovedFiles;
    }
  }, [droppedFiles, isDownloadingWatermarkRemovedFiles, isNonApiDocumentUploadEnabled]);

  const isDownloadOriginalFilesDisabled = useMemo(() => {
    if (droppedFiles.length === 0 || isDownloadingOriginalFiles) {
      return true;
    }
    return false;
  }, [droppedFiles, isDownloadingOriginalFiles]);

  const isValidateDisabled = useMemo(() => {
    if (droppedFiles.length === 0 || isValidating) {
      return true;
    }

    const invalidFiles = droppedFiles.filter(file => file.hasDuplicateNameValidationError !== false || !file.title);
    if (invalidFiles.length > 0) {
      return true;
    }

    if (clientDataSiteProvider?.allowDocumentSharing) {
      // Compute non-client-specific files that have a blobFilePath (these need WM removal)
      const nonClientSpecificFilesNeedingWm = droppedFiles.filter(
        f => !f.uploadClientSpecific && f.blobFilePath != null
      );

      // If no unchecked files need WM (all client-specific or no blob paths), skip the download gate
      if (nonClientSpecificFilesNeedingWm.length === 0) {
        return false;
      }

      // If watermarked files are present, only enable the Validate button once we have downloaded the files for verification
      return !isFilesDownloaded;
    }
  }, [isFilesDownloaded, droppedFiles, isValidating, clientDataSiteProvider]);

  const isSubmitDisabled = useMemo(() => {
    if (droppedFiles.length === 0) {
      return true;
    }

    // valid files are files that do not have any server validation errors
    // already existing non-watermarked documents do not count as valid documents for the purposes of enabling the submit button
    const validFiles = droppedFiles.filter(
      file => file.hasDuplicateNameValidationError == false && file.hasServerValidationError == false
    );

    // invalid files have a server validation error that is not equal to the 'documents already exist' errors
    const invalidFiles = droppedFiles.filter(file => {
      const hasAlreadyExistsError =
        file.error == Bixby.Constants.DOCUMENT_HOSTING_WATERMARKED_DOCUMENT_ALREADY_EXISTS ||
        file.error == Bixby.Constants.DOCUMENT_HOSTING_NON_WATERMARKED_DOCUMENT_ALREADY_EXISTS;

      return (
        file.hasDuplicateNameValidationError !== false ||
        (file.hasServerValidationError !== false && !hasAlreadyExistsError)
        // to be uncommented post inital doc upload phase
        // || (missingCategoriesError && hasTags != undefined && hasTags)
      );
    });

    return validFiles.length !== droppedFiles.length || invalidFiles.length > 0;
  }, [droppedFiles]);

  // To reset the flag when dropped files were cleared
  // To reset the flag when dropped files were cleared
  useEffect(() => {
    if (droppedFiles.length === 0) {
      setIsFilesDownloaded(false);
      return;
    }
    // Reset isFilesDownloaded if any non-client-specific file has a blobFilePath but no WM success
    // This handles the case where a file is unchecked from uploadClientSpecific
    if (isFilesDownloaded) {
      const hasUncheckedFilesNeedingWm = droppedFiles.some(
        f => !f.uploadClientSpecific && f.blobFilePath != null && f.isWatermarkRemoved !== true
      );
      if (hasUncheckedFilesNeedingWm) {
        setIsFilesDownloaded(false);
      }
    }
  }, [droppedFiles, isFilesDownloaded]);

  // Populate component with existing data when companyDocumentUploadByEmail is present
  useEffect(() => {
    if (companyDocumentUploadByEmail?.clientDataSiteProvider) {
      setClientDataSiteProvider(companyDocumentUploadByEmail.clientDataSiteProvider);
    }
    if (
      companyDocumentUploadByEmail?.preValidationResult &&
      companyDocumentUploadByEmail?.preValidationResult.length > 0
    ) {
      // Convert CompanyDocumentPrevalidationResult[] to IDocumentFile[]
      const results: Bixby.Models.IDocumentFile[] = companyDocumentUploadByEmail.preValidationResult.map(
        prevalidationResult => {
          // Create a placeholder File object since we don't have the actual file when loading from email
          const placeholderFile = new File([], prevalidationResult.fileName, { type: 'application/octet-stream' });

          const hasAlreadyExistsError =
            prevalidationResult.error === Bixby.Constants.DOCUMENT_HOSTING_NON_WATERMARKED_DOCUMENT_ALREADY_EXISTS ||
            prevalidationResult.error === Bixby.Constants.DOCUMENT_HOSTING_WATERMARKED_DOCUMENT_ALREADY_EXISTS;

          let calculatedTitle = prevalidationResult.fileName.split('/').pop();
          if (calculatedTitle && calculatedTitle.length > 0) {
            calculatedTitle = calculatedTitle.replace(/\.[^/.]+$/, '');
          }

          const documentFile: Bixby.Models.IDocumentFile = {
            id: uuidv4(),
            file: placeholderFile,
            fileName: prevalidationResult.fileName,
            title: calculatedTitle || prevalidationResult.fileName.replace(/\.[^/.]+$/, ''),
            description: prevalidationResult.description,
            companyDocumentCompanyDocumentCategories: prevalidationResult.companyDocumentCompanyDocumentCategories,
            rowNumber: prevalidationResult.rowNumber,
            index: prevalidationResult.index,
            error: prevalidationResult.error,
            isWatermarked: prevalidationResult.isWatermarked,
            documentDeclarationType: prevalidationResult.documentDeclarationType,
            blobFilePath: prevalidationResult.blobFilePath,
            hasDuplicateNameValidationError: false,
            hasServerValidationError:
              !prevalidationResult.isValid ||
              prevalidationResult.error === Bixby.Constants.DOCUMENT_HOSTING_MULTIPLE_EXCEL_MATCHES_FOUND ||
              hasAlreadyExistsError,
            requireAlreadyExistsOverride: hasAlreadyExistsError,
            datePosted: prevalidationResult.datePosted,
            submissionType: Bixby.Enums.CompanyDocumentSubmissionType.Files, // default to Files
          };

          return documentFile;
        }
      );

      setDroppedFiles(results);

      // Check if any files have errors
      const filesWithErrors = results.filter(
        file => file.error || file.hasDuplicateNameValidationError || file.hasServerValidationError
      );

      if (filesWithErrors.length > 0) {
        setPrevalidationError(`${filesWithErrors.length} file(s) have validation errors.`);
      }
    }
  }, [companyDocumentUploadByEmail]);

  const checkForDuplicates = (files: Bixby.Models.IDocumentFile[]) => {
    // check for duplicates
    const filesGroupedByName = groupBy(files, file => file.fileName);
    const dupes = Object.keys(filesGroupedByName).filter(_ => filesGroupedByName[_].length > 1);
    const previousDupes = Object.keys(filesGroupedByName).filter(
      _ => filesGroupedByName[_].length === 1 && filesGroupedByName[_][0].hasDuplicateNameValidationError === true
    );

    // mark previous dupes as valid
    previousDupes.forEach(nonDupeFileName => {
      const nonDupeFiles = filesGroupedByName[nonDupeFileName];
      nonDupeFiles[0].hasDuplicateNameValidationError = false;
    });

    dupes.forEach(dupeFileName => {
      const dupeFiles = filesGroupedByName[dupeFileName];

      // set to true by default
      dupeFiles.forEach(file => {
        file.hasDuplicateNameValidationError = true;
      });

      // if all files have a row number, and all files have a different row number, then set duplicate name validation error to false
      const allFilesHaveRowNumbers = dupeFiles.every(_ => !!_.rowNumber);
      if (allFilesHaveRowNumbers) {
        const allFilesHaveDifferentRowNumbers =
          Object.keys(groupBy(dupeFiles, _ => _.rowNumber)).length === dupeFiles.length;
        if (allFilesHaveDifferentRowNumbers) {
          dupeFiles.forEach(file => {
            file.hasDuplicateNameValidationError = false;
          });
        }
      }
    });
  };

  const handleDroppedFilesAndCheckDupes = async (files: Bixby.Models.IDocumentFile[]) => {
    // handle clear
    if (files.length <= 0) {
      setPrevalidationError(undefined);
      setValidationError(undefined);
      setDroppedFiles([]);
      setHasBeenValidated(false);
    }

    checkForDuplicates(files);
    setDroppedFiles(files);
    // Reset validation flag when files are added or modified
    setHasBeenValidated(false);
  };

  // Handle "Select All" for Upload Client Specific
  const handleSetAllUploadClientSpecific = (value: boolean) => {
    const updated = droppedFiles.map(f => ({ ...f, uploadClientSpecific: value }));
    setDroppedFiles(updated);
    setHasBeenValidated(false);
    // If unchecking all, reset isFilesDownloaded if any unchecked files have blobFilePath but no WM success
    if (!value) {
      const hasUncheckedFilesNeedingWm = updated.some(
        f => !f.uploadClientSpecific && f.blobFilePath != null && f.isWatermarkRemoved !== true
      );
      if (hasUncheckedFilesNeedingWm) {
        setIsFilesDownloaded(false);
      }
    }
  };

  const handleWatermarkRemovedFileDownload = async (blobStorageFilePath: string) => {
    try {
      const { blob, filename } = isNonApiDocumentUploadEnabled
        ? await DocumentsAdminService.downloadFilteredDocumentFiles(currentCompany?.id ?? '', {
            blobFiles: [
              {
                fullFileNameWithPath: blobStorageFilePath,
                blobFilePath: blobStorageFilePath,
              },
            ],
            isWatermarkRemoved: true,
          })
        : await DocumentsAdminService.downloadAllWatermarkRemovedFilesAsZip(currentCompany?.id ?? '', {
            watermarkRemovedBlobFilePaths: [blobStorageFilePath],
          });
      if (filename) dispatchDownloadDocument(blob, (filename.split(';')[0] || '').replace(/["]/g, ''));
      dispatchToastSuccess('Downloading document');
    } catch (err: any) {
      dispatchToastError('Error downloading document');
    }
  };

  const handleDownloadDocument = async (id: string, index?: number) => {
    const found = droppedFiles.find(_ => _.id === id);

    if (found && found.submissionType === Bixby.Enums.CompanyDocumentSubmissionType.Zip) {
      if (index && index > -1) {
        const { blob, filename } = await DocumentsAdminService.downloadFromZipFile(found.file, found.fileName, index);
        if (filename) {
          await dispatchDownloadDocument(blob, filename);
        }
        return;
      }

      const filesGroupedByName = groupBy(droppedFiles, file => file.fileName);
      const dupes = Object.keys(filesGroupedByName).filter(_ => filesGroupedByName[_].length > 1);
      index = undefined;
      if (dupes.length > 0 && dupes.includes(found.fileName)) {
        const foundIndex = filesGroupedByName[found.fileName].findIndex(_ => _.id === found.id);
        index = foundIndex > -1 ? foundIndex : undefined;
      }
      const { blob, filename } = await DocumentsAdminService.downloadFromZipFile(found.file, found.fileName, index);
      if (filename) {
        await dispatchDownloadDocument(blob, filename.split(';')[0]);
      }
    } else if (found && found.submissionType === Bixby.Enums.CompanyDocumentSubmissionType.Files) {
      await dispatchDownloadDocument(found.file, found.title);
    }
  };

  const handlePreValidateFiles = async (files: Bixby.Models.IDocumentFile[]) => {
    try {
      if (!currentCompany) return;
      setIsPrevalidating(true);
      const zipFiles = files.filter(_ => _.file.name.endsWith('.zip'));
      const areZipFilesUnique = zipFiles.every(_ => _.file.name === files[0].file.name);
      const nonZipFiles = files.filter(_ => !_.file.name.endsWith('.zip'));

      if (zipFiles.length > 1 && !areZipFilesUnique) {
        setPrevalidationError('Cannot submit more than one zip file at a time.');
        return;
      }

      const clientDataSiteProvider_Id = clientDataSiteProvider ? clientDataSiteProvider.id : undefined;

      if (nonZipFiles.length >= 1 && zipFiles.length == 0) {
        const uploadFiles: File[] = [];
        if (files.length > 0)
          for (const [, file] of files.entries()) {
            uploadFiles.push(new File([file.file], file.file.name, { type: file.file.type }));
          }
        const results = isNonApiDocumentUploadEnabled
          ? await DocumentsAdminService.manualPreValidateFiles(
              currentCompany.id,
              uploadFiles,
              clientDataSiteProvider_Id
            )
          : await DocumentsAdminService.prevalidateFiles(currentCompany.id, uploadFiles, clientDataSiteProvider_Id);
        const mapped = results.map(result => {
          const foundIx = files.findIndex(_ => _.file.name === result.fileName);

          const found = files[foundIx];
          found.title = result.title || found.title;
          found.description = result.description || found.description;
          found.companyDocumentCompanyDocumentCategories = result.companyDocumentCompanyDocumentCategories;
          found.submissionType = Bixby.Enums.CompanyDocumentSubmissionType.Files;
          found.rowNumber = result.rowNumber || undefined;
          found.hasDuplicateNameValidationError = false;
          found.error = result.error;
          found.isWatermarked = result.isWatermarked;
          found.documentDeclarationType = result.documentDeclarationType;
          found.blobFilePath = result.blobFilePath;
          const hasAlreadyExistsError =
            result.error === Bixby.Constants.DOCUMENT_HOSTING_NON_WATERMARKED_DOCUMENT_ALREADY_EXISTS ||
            result.error === Bixby.Constants.DOCUMENT_HOSTING_WATERMARKED_DOCUMENT_ALREADY_EXISTS;
          found.hasServerValidationError =
            !result.isValid ||
            result.error === Bixby.Constants.DOCUMENT_HOSTING_MULTIPLE_EXCEL_MATCHES_FOUND ||
            hasAlreadyExistsError;
          found.requireAlreadyExistsOverride = hasAlreadyExistsError;

          // remove from array in case we have duplicate files so it doesn't get picked up again
          files.splice(foundIx, 1);
          return found;
        });

        handleDroppedFilesAndCheckDupes(
          droppedFiles.concat([...(mapped.filter(_ => !!_) as Bixby.Models.IDocumentFile[])])
        );
      }

      if (zipFiles.length >= 1 && areZipFilesUnique && nonZipFiles.length == 0) {
        const results = isNonApiDocumentUploadEnabled
          ? await DocumentsAdminService.manualPrevalidateZip(
              currentCompany.id,
              zipFiles[0].file,
              clientDataSiteProvider_Id
            )
          : await DocumentsAdminService.prevalidateZip(currentCompany.id, zipFiles[0].file, clientDataSiteProvider_Id);

        const mapped = results.map(result => {
          let calculatedTitle = result.fileName.split('/').pop();
          if (calculatedTitle && calculatedTitle.length > 0) {
            calculatedTitle = calculatedTitle.replace(/\.[^/.]+$/, '');
          }

          const model: Bixby.Models.IDocumentFile = {
            id: uuidv4(),
            fileName: result.fileName,
            title: calculatedTitle || result.fileName.replace(/\.[^/.]+$/, ''),
            description: result.description,
            file: zipFiles[0].file,
            companyDocumentCompanyDocumentCategories: result.companyDocumentCompanyDocumentCategories,
            rowNumber: result.rowNumber,
            index: result.index,
            hasDuplicateNameValidationError: results.filter(_ => _.fileName === result.fileName).length > 1,
            error: result.error,
            submissionType: Bixby.Enums.CompanyDocumentSubmissionType.Zip,
            documentDeclarationType: result.documentDeclarationType,
            isWatermarked: result.isWatermarked,
            blobFilePath: result.blobFilePath,
          };
          const hasAlreadyExistsError =
            result.error === Bixby.Constants.DOCUMENT_HOSTING_NON_WATERMARKED_DOCUMENT_ALREADY_EXISTS ||
            result.error === Bixby.Constants.DOCUMENT_HOSTING_WATERMARKED_DOCUMENT_ALREADY_EXISTS;
          model.hasServerValidationError =
            !result.isValid ||
            result.error === Bixby.Constants.DOCUMENT_HOSTING_MULTIPLE_EXCEL_MATCHES_FOUND ||
            hasAlreadyExistsError;
          model.requireAlreadyExistsOverride = hasAlreadyExistsError;

          return model;
        });

        handleDroppedFilesAndCheckDupes(
          droppedFiles.concat([...(mapped.filter(_ => !!_) as Bixby.Models.IDocumentFile[])])
        );
      }
      dispatchToastSuccess('Prevalidation check complete');
    } catch (err: any) {
      dispatchToastError('Error prevalidating documents');
    } finally {
      setIsPrevalidating(false);
    }
  };

  const handleRemoveWatermark = async () => {
    if (!currentCompany) return;
    try {
      setIsRemovingWatermark(true);
      setValidationError(undefined);

      const allFilePaths: string[] = droppedFiles
        .filter(
          _ =>
            !_.uploadClientSpecific &&
            _.blobFilePath != undefined &&
            _.blobFilePath != null &&
            _.blobFilePath != '' &&
            _.fileName.toLowerCase().endsWith('.pdf')
        )
        .map(_ => _.blobFilePath as string);

      const input: Bixby.Models.CompanyDocumentRemoveWatermark = {
        clientName: clientDataSiteProvider?.client_Name ?? '',
        blobNames: allFilePaths,
      };
      const result = isNonApiDocumentUploadEnabled
        ? await DocumentsAdminService.sharedRemoveWatermark(input)
        : await DocumentsAdminService.removeWatermark(input);

      let droppedFilesTemp = droppedFiles;
      result.allResponses.forEach(response => {
        droppedFilesTemp = droppedFilesTemp.map(file =>
          file.blobFilePath === response.inputFileName
            ? {
                ...file,
                isWatermarkRemoved: response.watermarkRemoved,
                watermarkRemovedBlobFilePath: response.watermarkRemoved ? response.outputBlobName : undefined,
              }
            : { ...file }
        );
      });
      setDroppedFiles(droppedFilesTemp);
      // Reset validation flag since files have been modified with watermark removal
      setHasBeenValidated(false);

      if (result.allSuccess && result.allWatermarkRemoved) {
        dispatchToastSuccess('Watermark Removed.');
      } else {
        dispatchToastError('Unable to remove Watermark');
      }
    } catch (err: any) {
      dispatchToastError('Watermark Removal failed');
      if (err && err.response && err.response.data && err.response.data.error) {
        setValidationError(err.response.data.error);
      } else {
        setValidationError('Error removing watermark');
      }
    } finally {
      setIsRemovingWatermark(false);
    }
  };

  const handleValidation = async () => {
    try {
      if (!currentCompany) return;

      setIsValidating(true);
      setValidationError(undefined);

      const documents = droppedFiles.map((file: Bixby.Models.IDocumentFile) => {
        const model: FileValidationRequest = {
          id: file.id,
          fileName:
            file.submissionType == Bixby.Enums.CompanyDocumentSubmissionType.Zip ? file.fileName : file.file.name,
          title: file.title,
          description: file.description || '',
          companyDocumentCategory_Ids: file.companyDocumentCompanyDocumentCategories
            ? file.companyDocumentCompanyDocumentCategories.map(_ => _.companyDocumentCategory_Id)
            : [],
          clientDataSiteProvider_Id: (clientDataSiteProvider || {}).id,
          rowNumber: file.rowNumber,
          index: file.index,
          overrideAlreadyExistsError: file.overrideAlreadyExistsError,
        };
        return model;
      });
      const results = await DocumentsAdminService.validateDocuments(currentCompany.id, documents);
      const mapped = results.map(result => {
        const found = droppedFiles.find(_ => _.id === result.inputFileId);
        if (!found) return;
        found.error = result.error;
        found.rowNumber = result.rowNumber;
        found.description = result.description;
        found.isWatermarked = result.isWatermarked;
        found.documentDeclarationType = result.documentDeclarationType;
        // duplicate, non-watermarked files will have a server validation error but are considered valid
        // files with multiple excel matches are valid but will also have an error
        const hasAlreadyExistsError =
          result.error === Bixby.Constants.DOCUMENT_HOSTING_NON_WATERMARKED_DOCUMENT_ALREADY_EXISTS ||
          result.error === Bixby.Constants.DOCUMENT_HOSTING_WATERMARKED_DOCUMENT_ALREADY_EXISTS;
        found.hasServerValidationError =
          !result.isValid ||
          result.error === Bixby.Constants.DOCUMENT_HOSTING_MULTIPLE_EXCEL_MATCHES_FOUND ||
          result.error === Bixby.Constants.DOCUMENT_HOSTING_INVALID_ROW_NUMBER ||
          hasAlreadyExistsError;
        found.requireAlreadyExistsOverride = found.requireAlreadyExistsOverride || hasAlreadyExistsError;
        return found;
      });
      checkForDuplicates([...(mapped.filter(_ => !!_) as Bixby.Models.IDocumentFile[])]);
      setDroppedFiles([...(mapped.filter(_ => !!_) as Bixby.Models.IDocumentFile[])]);
      dispatchToastSuccess('Validation check complete.');
      setHasBeenValidated(true);
    } catch (err: any) {
      dispatchToastError('Validation check failed');
      if (err && err.response && err.response.data && err.response.data.error) {
        setValidationError(err.response.data.error);
      } else {
        setValidationError('Error validating documents');
      }
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (!currentCompany) return;

      setIsSubmitting(true);
      setUploadError(undefined);

      const documents = droppedFiles
        .filter((file: Bixby.Models.IDocumentFile) => {
          // remove files that already exist from the list of documents to submit unless they have been overridden
          // this simplifies the file uploading process for the user
          if (
            file.error === Bixby.Constants.DOCUMENT_HOSTING_WATERMARKED_DOCUMENT_ALREADY_EXISTS ||
            file.error === Bixby.Constants.DOCUMENT_HOSTING_NON_WATERMARKED_DOCUMENT_ALREADY_EXISTS
          ) {
            return file.overrideAlreadyExistsError;
          }
          return true;
        })
        .map((file: Bixby.Models.IDocumentFile) => {
          const model: Bixby.Models.CompanyDocumentSubmission = {
            id: file.id,
            title: file.title,
            fullFileName: file.fileName,
            description: file.description || '',
            companyDocumentCompanyDocumentCategories: file.companyDocumentCompanyDocumentCategories || [],
            clientDataSiteProvider_Id: (clientDataSiteProvider || {}).id,
            companyDocumentTags: file.tags,
            hasFile: false,
            datePosted: file.datePosted,
            rowNumber: file.rowNumber,
            index: file.index,
            overrideAlreadyExistsError: file.overrideAlreadyExistsError,
            sendEmailChecked: sendEmailValue,
            blobOriginalStorageFilePath: file.blobFilePath,
            watermarkRemovedBlobStorageFilePath: file.watermarkRemovedBlobFilePath,
            uploadClientSpecific: file.uploadClientSpecific ?? false,
          };
          return model;
        });
      const individualFiles = droppedFiles.filter(
        _ =>
          _.submissionType == Bixby.Enums.CompanyDocumentSubmissionType.Files && documents.find(doc => doc.id === _.id)
      );
      const zipFiles = droppedFiles.filter(
        _ => _.submissionType == Bixby.Enums.CompanyDocumentSubmissionType.Zip && documents.find(doc => doc.id === _.id)
      );

      if (individualFiles.length > 0) {
        // submitting files all at once
        const docs: Bixby.Models.CompanyDocumentSubmission[] = [];
        const files: File[] = [];
        for (const [, file] of individualFiles.entries()) {
          const doc = documents.find(_ => _.id === file.id);
          if (!doc) continue;
          files.push(new File([file.file], file.file.name, { type: file.file.type }));
          docs.push(doc);
        }

        if (isNonApiDocumentUploadEnabled) {
          await DocumentsAdminService.sharedSubmitBulkDocuments(currentCompany.id, docs);
        } else {
          await DocumentsAdminService.submitBulkDocuments(currentCompany.id, docs, files);
        }
      }

      if (zipFiles.length > 0) {
        if (isNonApiDocumentUploadEnabled) {
          await DocumentsAdminService.manualSubmitZipFile(
            currentCompany.id,
            documents.filter(_ => zipFiles.map(_ => _.id).includes(_.id))
          );
        } else {
          await DocumentsAdminService.submitZipFile(
            currentCompany.id,
            zipFiles[0].file,
            documents.filter(_ => zipFiles.map(_ => _.id).includes(_.id))
          );
        }
      }

      dispatchToastSuccess('Successfully submitted documents.');
      setIsSubmitting(false);
      setHasBeenValidated(false);
      setUploadSuccessful(true);
    } catch (err: any) {
      dispatchToastError(`Document upload error`);
      if (err && err.response && err.response.data && err.response.data.error) {
        setUploadError(`${err.response.data.error}`);
      } else {
        setUploadError('');
      }
      setIsSubmitting(false);
      setHasBeenValidated(false);
    }
  };

  const downloadWatermarkRemovedFiles = async () => {
    try {
      if (!currentCompany) return;
      setIsDownloadingWatermarkRemovedFiles(true);
      const input = isNonApiDocumentUploadEnabled
        ? {
            blobFiles: droppedFiles
              .filter(_ => !Bixby.Utils.isNullOrEmpty(_.watermarkRemovedBlobFilePath))
              .map(_ => ({
                fullFileNameWithPath: _.fileName ?? '',
                blobFilePath: _.watermarkRemovedBlobFilePath ?? '',
              })),
            isWatermarkRemoved: true,
          }
        : {
            watermarkRemovedBlobFilePaths: droppedFiles
              .filter(_ => !Bixby.Utils.isNullOrEmpty(_.watermarkRemovedBlobFilePath))
              .map(_ => _.watermarkRemovedBlobFilePath ?? ''),
          };
      const result = isNonApiDocumentUploadEnabled
        ? await DocumentsAdminService.downloadFilteredDocumentFiles(
            currentCompany.id,
            input as Bixby.Models.CompanyDocumentFilesDownload
          )
        : await DocumentsAdminService.downloadAllWatermarkRemovedFilesAsZip(
            currentCompany.id,
            input as Bixby.Models.CompanyDocumentWatermarkRemovedFiles
          );

      // If Result is success then update dropped files state
      if (result) {
        const { blob, filename } = result;
        if (filename) {
          await dispatchDownloadDocument(blob, filename.split(';')[0]);
        }
        setIsFilesDownloaded(true);
        dispatchToastSuccess('Downloaded Watermark Removed Files.');
      } else {
        dispatchToastError('Unable to download files.');
      }
      return;
    } catch (err: any) {
      dispatchToastError('Download failed');
    } finally {
      setIsDownloadingWatermarkRemovedFiles(false);
    }
  };

  const downloadOriginalFiles = async () => {
    try {
      if (!currentCompany) return;
      setIsDownloadingOriginalFiles(true);
      const zip = new JSZip();
      const individualFiles = droppedFiles.filter(
        _ => _.submissionType == Bixby.Enums.CompanyDocumentSubmissionType.Files
      );
      const zipFiles = droppedFiles.filter(_ => _.submissionType == Bixby.Enums.CompanyDocumentSubmissionType.Zip);

      if (zipFiles.length > 0) {
        const filename = `original-files-${currentCompany.id}.zip`;
        if (zipFiles[0].file) {
          await dispatchDownloadDocument(zipFiles[0].file, filename);
          dispatchToastSuccess('Downloaded Original Zip File.');
        } else {
          dispatchToastError('Unable to download file.');
        }
      } else if (individualFiles.length > 0) {
        if (individualFiles.length === 1) {
          if (individualFiles[0].file) {
            await dispatchDownloadDocument(individualFiles[0].file, individualFiles[0].fileName);
            dispatchToastSuccess('Downloaded Original File.');
          }
        } else {
          const filesToBeDownloaded = individualFiles.map(_ => {
            return {
              file: _.file,
              name: _.fileName,
            };
          });
          filesToBeDownloaded.forEach(file => zip.file(file.name, file.file));
          const combined = await zip.generateAsync({ type: 'blob' });
          const filename = `original-files-${currentCompany.id}.zip`;
          if (combined) {
            await dispatchDownloadDocument(combined, filename);
            dispatchToastSuccess('Downloaded Original Files.');
          } else {
            dispatchToastError('Unable to download files.');
          }
        }
      }
      return;
    } catch (err: any) {
      dispatchToastError('Download failed');
    } finally {
      setIsDownloadingOriginalFiles(false);
    }
  };

  const downloadOriginalFilesFromContainer = async () => {
    try {
      if (!currentCompany) return;
      setIsDownloadingOriginalFiles(true);
      const input: Bixby.Models.CompanyDocumentFilesDownload = {
        blobFiles: droppedFiles
          .filter(_ => !Bixby.Utils.isNullOrEmpty(_.blobFilePath))
          .map(_ => ({
            fullFileNameWithPath: _.fileName,
            blobFilePath: _.blobFilePath ?? '',
          })),
        isWatermarkRemoved: false,
      };
      const result = await DocumentsAdminService.downloadFilteredDocumentFiles(currentCompany.id, input);

      // If Result is success then download the file
      if (result) {
        const { blob, filename } = result;
        if (filename) {
          await dispatchDownloadDocument(blob, filename.split(';')[0]);
        }
        dispatchToastSuccess('Downloaded Original Files.');
      } else {
        dispatchToastError('Unable to download files.');
      }
      return;
    } catch (err: any) {
      dispatchToastError('Download failed');
    } finally {
      setIsDownloadingOriginalFiles(false);
    }
  };

  const handleClientDataSiteProviderValidation = (model: Bixby.Models.ClientDataSiteProvider) => {
    setClientDataSiteProvider(model);
  };
  if (isLoading) {
    return <Loading pageLevel />;
  } else if (uploadSuccessful) {
    return <Navigate to={`/admin/company/${currentCompany?.id}/document-hosting`} />;
  } else {
    return companyDocumentCategories ? (
      <section className="file-upload">
        {clientDataSiteProvider?.allowDocumentSharing === false && (
          <p className="error watermark-error">
            This is a locked watermarked site. Watermark Removal Tool not permitted for this client.
          </p>
        )}
        <h4>Step 1 - Enter Client Identifier ({currentCompany?.dataSiteProvider_Name})</h4>
        <ClientValidation
          currentCompanyId={currentCompany?.id ?? ''}
          clientDataSiteProvider={clientDataSiteProvider}
          onValidityChange={handleClientDataSiteProviderValidation}
          isFromWorkflowManagement={!!companyDocumentUploadByEmail}
        />
        <h4>
          Step 2 - Upload Files{clientDataSiteProvider?.client_Name ? ` for ${clientDataSiteProvider.client_Name}` : ''}
        </h4>
        {!!prevalidationError && <p className="error">{prevalidationError}</p>}
        <CompanyDocumentFileUpload
          currentCompany={currentCompany}
          companyDocumentCategories={companyDocumentCategories}
          droppedFiles={droppedFiles}
          onSetDroppedFiles={handleDroppedFilesAndCheckDupes}
          onPreValidateFiles={handlePreValidateFiles}
          clientDataSiteProvider={clientDataSiteProvider}
          isProcessingFiles={isPreValidating}
          dispatchToastError={dispatchToastError}
          onDownloadDocument={handleDownloadDocument}
          onDownloadWatermarkRemovedDocument={handleWatermarkRemovedFileDownload}
          onSetAllUploadClientSpecific={handleSetAllUploadClientSpecific}
          isFromWorkflowManagement={!!companyDocumentUploadByEmail}
        />
        {clientDataSiteProvider?.allowDocumentSharing && (
          <div className="remove-watermark">
            <BixButton
              type="button"
              useSmall
              disabled={isRemoveWatermarkDisabled}
              useLoading
              isLoading={isRemovingWatermark}
              onClick={handleRemoveWatermark}>
              Remove Watermark
            </BixButton>
            <BixButton
              type="button"
              useSmall
              disabled={isDownloadWatermarkRemovedFilesDisabled}
              useLoading
              isLoading={isDownloadingWatermarkRemovedFiles}
              onClick={downloadWatermarkRemovedFiles}>
              Download Watermark Removed Files
            </BixButton>
            <BixButton
              type="button"
              useSmall
              disabled={isDownloadOriginalFilesDisabled}
              useLoading
              isLoading={isDownloadingOriginalFiles}
              onClick={isNonApiDocumentUploadEnabled ? downloadOriginalFilesFromContainer : downloadOriginalFiles}>
              Download Original Files
            </BixButton>
          </div>
        )}
        <h4>Step 3 - Validation</h4>
        <div className="server-validation-contain">
          <BixButton
            type="button"
            useSmall
            disabled={isValidateDisabled}
            useLoading
            isLoading={isValidating}
            onClick={handleValidation}>
            Validate
          </BixButton>
        </div>
        {!!validationError && <p className="error">{validationError}</p>}
        <h4>Step 4 - Submit</h4>
        {hasEmailAlertPermissions && (
          <div className="acceptance_wrap">
            <BixCheckbox
              id="sendEmailValue"
              checked={sendEmailValue}
              onChange={event => {
                const target = event.target as HTMLInputElement;
                setSendEmailValue(target.checked);
              }}>
              Send document upload alert to clients.
            </BixCheckbox>
          </div>
        )}
        <div className="server-validation-contain">
          <BixButton
            type="button"
            useSmall
            disabled={isSubmitDisabled || !hasBeenValidated}
            useLoading
            isLoading={isSubmitting}
            onClick={handleSubmit}>
            Submit
          </BixButton>
        </div>
        {!!isSubmitting && <p>Uploading documents...</p>}
        {!!uploadError && (
          <>
            <p className="error">{`Error submitting document`}</p>
            <p className="error">{uploadError}</p>
          </>
        )}
      </section>
    ) : (
      <></>
    );
  }
};

const mapStateToProps = (state: AppState) => {
  const { isNonApiDocumentUploadEnabled } = getNonApiDocumentUploadEnabled(state);

  return {
    currentCompany: state.companyState.currentCompany,
    isNonApiDocumentUploadEnabled: isNonApiDocumentUploadEnabled,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchDownloadDocument: (blob: Blob, fileName: string) => dispatch(downloadFile(blob, fileName)),
  dispatchDownloadFailure: () => dispatch(toastError('Failed to download document')),
  dispatchToastSuccess: (message: string) => dispatch(toastSuccess(message)),
  dispatchToastError: (message: string) => dispatch(toastError(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyDocumentsUpload);
